import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inicioDe } from "@/lib/inicio";
import { descargar } from "@/lib/download";
import { toast } from "sonner";
import { Menu, MoreVertical, Package, Boxes, CheckCircle2, ScanLine, Search, SlidersHorizontal, Download, LogOut, RefreshCw } from "lucide-react";
import { Escaner } from "@/components/empaque/Escaner";
import { Felicidades } from "@/components/Felicidades";
import { PedidoPaquetes } from "@/components/empaque/PaqueteVista";
import { CerrarDia, Cierres } from "@/components/empaque/CierreEmpaque";

const TABS = [["pendientes", "Pendientes", "tab_pendientes"], ["hoy", "Empacados hoy", "tab_hoy"], ["historial", "Historial", null]];

const Contador = ({ icon: Icon, label, n, color, id }) => (
  <div className="rounded-xl border-2 border-zinc-200 bg-white p-2 text-center shadow-sm" data-testid={id}>
    <Icon className={`w-6 h-6 mx-auto ${color}`} />
    <div className="text-sm sm:text-base font-semibold">{label}</div>
    <div className={`text-3xl sm:text-4xl font-black leading-none ${color}`}>{n}</div>
  </div>
);

const Elegir = ({ codigo, opciones, onPick, onClose }) => (
  <div className="fixed inset-0 z-[70] bg-white flex flex-col" data-testid="emp-elegir">
    <div className="bg-black text-white p-4"><div className="text-lg font-black">Guía nueva: ¿a qué paquete pertenece?</div><div className="font-mono text-2xl break-all">{codigo}</div></div>
    <div className="flex-1 overflow-y-auto p-3 space-y-2">
      {opciones.map((o) => (
        <button key={`${o.id}-${o.n}`} onClick={() => onPick(o)} className="w-full text-left rounded-lg border-2 border-zinc-200 p-3" data-testid={`emp-elegir-${o.numero}-${o.num}`}>
          <div className="text-xl font-black">{o.numero} · PAQUETE {o.num} de {o.de}</div>
          <div className="text-sm">{o.cliente} — {o.productos.map((p) => `${p.cantidad}× ${p.producto}`).join(", ") || "sin productos"}</div>
        </button>
      ))}
      {opciones.length === 0 && <div className="text-center text-zinc-500 py-10">No hay paquetes pendientes sin guía. Registre la guía en el Organizador de Paquetes.</div>}
    </div>
    <button onClick={onClose} className="m-3 h-14 rounded-lg border-2 border-zinc-300 text-lg font-bold uppercase" data-testid="emp-elegir-cancelar">Cancelar</button>
  </div>
);

export default function AppEmpaque() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pendientes");
  const [data, setData] = useState({ items: [], contadores: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [rango, setRango] = useState({ desde: "", hasta: "" });
  const [filtros, setFiltros] = useState(false);
  const [menu, setMenu] = useState(false);
  const [scan, setScan] = useState(false);
  const [ult, setUlt] = useState(null);
  const [fel, setFel] = useState(null);
  const [elegir, setElegir] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/app-empaque/paquetes", { params: { vista: tab, ...rango } })).data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [tab, rango]);
  useEffect(() => { setLoading(true); load(); const t = setInterval(() => { if (!document.hidden && !scan) load(); }, 30000); return () => clearInterval(t); }, [load, scan]);

  const sinGuia = async (codigo) => {
    const { data: d } = await api.get("/app-empaque/paquetes", { params: { vista: "pendientes" } });
    const op = d.items.flatMap((r) => r.paquetes.filter((p) => !p.guia).map((p) => ({ ...p, id: r.id, numero: r.numero, cliente: r.cliente })));
    setScan(false);
    setElegir({ codigo, opciones: op });
  };
  const leer = async (codigo) => {
    try {
      const { data: r } = await api.post("/app-empaque/escanear-paquete", { codigo });
      setUlt({ ok: true, txt: `${r.pedido} · ${r.paquete} escaneado (${r.escaneados}/${r.total})${r.aviso ? ` · ${r.aviso}` : ""}` });
      const falta = r.total - r.escaneados;
      setFel(r.empacado
        ? { id: Date.now(), completo: true, titulo: "¡FELICIDADES! 🎉", texto: `${r.pedido} EMPACADO` }
        : { id: Date.now(), completo: false, titulo: "¡FELICIDADES! 🎉", texto: `${r.pedido}: ${falta === 1 ? "falta 1 paquete" : `faltan ${falta} paquetes`}` });
      if (navigator.vibrate) navigator.vibrate(120);
      load();
    } catch (e) {
      if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
      if (e.response?.status === 404) return sinGuia(codigo);
      setUlt({ ok: false, txt: formatApiError(e) });
    }
  };
  const vincular = async (o) => {
    const c = elegir.codigo;
    setElegir(null);
    try { await api.post(`/app-empaque/${o.id}/paquete-guia`, { guia: c, n: o.n || null }); setScan(true); await leer(c); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const s = q.trim().toLowerCase();
  const items = data.items.filter((r) => !s || [r.numero, r.cliente, ...r.paquetes.flatMap((p) => [p.guia, ...p.productos.map((x) => x.producto)])].some((v) => (v || "").toLowerCase().includes(s)));
  const exportar = (fmt) => { setMenu(false); descargar(`/app-empaque/reporte.${fmt}?${new URLSearchParams({ vista: tab, ...rango })}`, `empaque_${tab}.${fmt}`).catch((e) => toast.error(String(e.message || e))); };
  const c = data.contadores || {};

  return (
    <div className="min-h-screen bg-white text-black" data-testid="app-empaque">
      <header className="sticky top-0 z-40 bg-[#FFD600] shadow-md">
        <div className="px-3 h-16 flex items-center justify-between gap-2">
          <button onClick={() => nav(inicioDe(user))} className="p-1" aria-label="Inicio" data-testid="emp-home"><Menu className="w-8 h-8" /></button>
          <div className="font-black text-xl sm:text-3xl tracking-tight text-center">Empaque – Departamentos</div>
          <div className="relative">
            <button onClick={() => setMenu((x) => !x)} className="p-1" aria-label="Más opciones" data-testid="emp-menu"><MoreVertical className="w-7 h-7" /></button>
            {menu && (
              <div className="absolute right-0 mt-1 w-48 rounded-lg bg-white shadow-xl border z-50 overflow-hidden">
                <button onClick={() => exportar("xlsx")} className="w-full flex items-center gap-2 px-3 py-3 font-bold hover:bg-zinc-100" data-testid="emp-xlsx"><Download className="w-4 h-4" /> Exportar Excel</button>
                <button onClick={() => exportar("pdf")} className="w-full flex items-center gap-2 px-3 py-3 font-bold hover:bg-zinc-100" data-testid="emp-pdf"><Download className="w-4 h-4" /> Exportar PDF</button>
                <button onClick={() => { setMenu(false); load(); }} className="w-full flex items-center gap-2 px-3 py-3 font-bold hover:bg-zinc-100" data-testid="emp-refresh"><RefreshCw className="w-4 h-4" /> Actualizar</button>
                <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-3 font-bold hover:bg-zinc-100" data-testid="emp-logout"><LogOut className="w-4 h-4" /> Salir</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="px-3 pt-3 pb-24 max-w-4xl mx-auto">
        <div className="grid grid-cols-4 gap-2 mb-3">
          <Contador icon={Package} label="Pedidos" n={c.pedidos ?? 0} color="text-black" id="emp-cnt-pedidos" />
          <Contador icon={Boxes} label="Paquetes" n={c.paquetes ?? 0} color="text-black" id="emp-cnt-paquetes" />
          <Contador icon={CheckCircle2} label="Escaneados" n={c.escaneados ?? 0} color="text-emerald-600" id="emp-cnt-escaneados" />
          <Contador icon={Package} label="Pendientes" n={c.pendientes ?? 0} color="text-red-600" id="emp-cnt-pendientes" />
        </div>
        <button onClick={() => { setUlt(null); setFel(null); setScan(true); }} className="w-full h-16 sm:h-20 mb-3 rounded-xl bg-black text-white text-2xl sm:text-3xl font-black uppercase flex items-center justify-center gap-3" data-testid="emp-scan-global">
          <ScanLine className="w-9 h-9" /> Escanear paquetes
        </button>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {TABS.map(([v, l, k]) => (
            <button key={v} onClick={() => setTab(v)} className={`h-12 rounded-lg font-black text-xs sm:text-lg whitespace-nowrap px-1 ${tab === v ? "bg-[#FFD600] text-black" : "bg-black text-white"}`} data-testid={`emp-tab-${v}`}>
              {l}{k ? ` (${c[k] ?? 0})` : ""}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1"><Search className="w-6 h-6 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido, cliente o producto..." className="w-full h-12 rounded-lg border-2 border-zinc-300 pl-12 pr-3 text-sm sm:text-base" data-testid="emp-buscar" /></div>
          <button onClick={() => setFiltros((x) => !x)} className="h-12 w-16 rounded-lg border-2 border-zinc-300 flex items-center justify-center" aria-label="Filtros" data-testid="emp-filtros"><SlidersHorizontal className="w-6 h-6" /></button>
        </div>
        {filtros && (
          <div className="mb-3 space-y-2 rounded-lg border-2 border-zinc-200 p-2" data-testid="emp-filtros-panel">
            {tab === "historial" && (
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} className="h-11 rounded-lg border-2 border-zinc-300 px-2" data-testid="emp-desde" />
                <input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} className="h-11 rounded-lg border-2 border-zinc-300 px-2" data-testid="emp-hasta" />
              </div>
            )}
            {tab === "hoy" && <CerrarDia cont={{ empacados_hoy: c.tab_hoy ?? 0, pendientes: c.tab_pendientes ?? 0 }} onDone={load} />}
            {tab === "historial" && <Cierres onChanged={load} />}
            {tab === "pendientes" && <div className="text-sm text-zinc-500">Sin filtros adicionales en Pendientes.</div>}
          </div>
        )}
        {loading ? <div className="text-center py-16 text-zinc-500 text-lg">Cargando…</div>
          : items.length === 0 ? <div className="text-center py-20"><Package className="w-14 h-14 text-zinc-300 mx-auto mb-3" /><div className="text-zinc-600 font-bold text-lg">Sin pedidos</div></div>
          : <div className="space-y-3">{items.map((r) => <PedidoPaquetes key={r.id} r={r} />)}</div>}
      </main>
      {scan && (
        <Escaner titulo="ESCANEAR PAQUETES" placeholder="Código de barras o guía del paquete" continuo onCode={leer} onClose={() => { setScan(false); load(); }}>
          {ult && <div className={`p-3 text-base font-bold ${ult.ok ? "bg-emerald-600" : "bg-red-700"}`} data-testid="emp-scan-resultado">{ult.txt}</div>}
          <Felicidades msg={fel} />
        </Escaner>
      )}
      {elegir && <Elegir codigo={elegir.codigo} opciones={elegir.opciones} onClose={() => setElegir(null)} onPick={vincular} />}
    </div>
  );
}
